import React, { useEffect, useState } from "react";
import {
  Button, Input, Select, Space, Typography, Table, Modal,
  TextArea, Spin, Toast, Card, Tag, Collapse,
} from "@douyinfe/semi-ui";
import { IconPlus, IconSearch, IconPlay } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;
const api = ApiClient.getDefault();

interface OpsApiItem {
  id: number;
  name: string;
  method: string;
  url: string;
  headers: string;
  bodyTemplate: string;
  groupName: string;
  source: string;
  remark: string;
}

interface ParsedEndpoint {
  className: string;
  methodName: string;
  method: string;
  url: string;
  description: string;
}

interface ExecuteResult {
  success: boolean;
  statusCode: number;
  body: string;
  contentType: string;
  error: string;
}

const METHOD_COLORS: Record<string, string> = {
  GET: "blue", POST: "green", PUT: "orange", DELETE: "red", PATCH: "purple",
};

const METHOD_OPTIONS = ["GET", "POST", "PUT", "DELETE", "PATCH"].map(m => ({ value: m, label: m }));

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 8 }}>
      <div style={{ width: 70, textAlign: "right", flexShrink: 0, fontSize: 13, color: "#666" }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}



/* ==================== 功能导航块 ==================== */

const NAV_ITEMS = [
  { path: "/scheduledTask", label: "定时任务", icon: "", desc: "定时任务调度管理" },
  { path: "/redisManager", label: "缓存管理", icon: "", desc: "Redis缓存查看与管理" },
  { path: "/llmConfig", label: "LLM配置", icon: "", desc: "大模型参数配置" },
  { path: "/logViewer", label: "日志管理", icon: "", desc: "实时日志搜索与查看" },
];

function NavCards() {
  const navigate = useNavigate();
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: 12, marginBottom: 16,
    }}>
      {NAV_ITEMS.map(item => (
        <div key={item.path} onClick={() => navigate(item.path)} style={{
          padding: 16, background: "var(--semi-color-bg-0)", borderRadius: 8,
          border: "1px solid var(--semi-color-border)", cursor: "pointer",
          transition: "box-shadow 0.2s, border-color 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--semi-color-primary)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--semi-color-border)"; e.currentTarget.style.boxShadow = "none"; }}
        >
          <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
          <Text strong style={{ fontSize: 14, display: "block" }}>{item.label}</Text>
          <Text size="small" type="tertiary">{item.desc}</Text>
        </div>
      ))}
    </div>
  );
}

/* ==================== 主页面 ==================== */

function OpsMain() {
  const navigate = useNavigate();
  const [apis, setApis] = useState<OpsApiItem[]>([]);
  const [selected, setSelected] = useState<OpsApiItem | null>(null);
  const [ef, setEf] = useState<Partial<OpsApiItem>>({});
  const [loading, setLoading] = useState(false);
  const [execLoading, setExecLoading] = useState(false);
  const [result, setResult] = useState<ExecuteResult | null>(null);

  const [parseVisible, setParseVisible] = useState(false);
  const [parseClass, setParseClass] = useState("");
  const [parsed, setParsed] = useState<ParsedEndpoint[]>([]);
  const [selParsed, setSelParsed] = useState<number[]>([]);
  const [parseLoading, setParseLoading] = useState(false);

  const [addVisible, setAddVisible] = useState(false);
  const [af, setAf] = useState<Partial<OpsApiItem>>({ method: "GET", groupName: "default", source: "MANUAL" });

  const loadApis = async () => {
    setLoading(true);
    try {
      const resp = await api.request<OpsApiItem[]>("/ops/apis", "GET");
      setApis(resp.data || []);
    } catch { } finally { setLoading(false); }
  };
  useEffect(() => { loadApis(); }, []);

  const selectApi = (api: OpsApiItem) => { setSelected(api); setEf({ ...api }); setResult(null); };

  const doExec = async () => {
    setExecLoading(true);
    try {
      const resp = await api.request<ExecuteResult>("/ops/execute", "POST", undefined, {
        method: ef.method || "GET", url: ef.url || "", headers: ef.headers || "", body: ef.bodyTemplate || "",
      });
      setResult(resp.data);
    } catch (e: any) { Toast.error(e.message); } finally { setExecLoading(false); }
  };

  const doSave = async () => {
    try {
      if (ef.id) {
        await api.request(`/ops/apis/${ef.id}`, "PUT", undefined, ef);
        Toast.success("已更新");
      } else {
        await api.request("/ops/apis", "POST", undefined, ef);
        Toast.success("已创建");
      }
      loadApis();
    } catch (e: any) { Toast.error(e.message); }
  };

  const doDelete = async (id: number) => {
    try {
      await api.request(`/ops/apis/${id}`, "DELETE");
      Toast.success("已删除");
      if (selected?.id === id) { setSelected(null); setEf({}); setResult(null); }
      loadApis();
    } catch (e: any) { Toast.error(e.message); }
  };

  const doAdd = async () => {
    try {
      await api.request("/ops/apis", "POST", undefined, af);
      Toast.success("已添加");
      setAddVisible(false);
      setAf({ method: "GET", groupName: "default", source: "MANUAL" });
      loadApis();
    } catch (e: any) { Toast.error(e.message); }
  };

  const doParse = async () => {
    setParseLoading(true);
    try {
      const resp = await api.request<ParsedEndpoint[]>("/ops/parse-controller", "POST", undefined, { className: parseClass });
      setParsed(resp.data || []);
      setSelParsed([]);
    } catch (e: any) { Toast.error(e.message); } finally { setParseLoading(false); }
  };

  const doBatchSave = async () => {
    const toSave = selParsed.map(i => parsed[i]).map(ep => ({
      name: `${ep.className}.${ep.methodName}${ep.description ? " - " + ep.description : ""}`,
      method: ep.method, url: ep.url, headers: "", bodyTemplate: "",
      groupName: ep.className, source: "PARSED", remark: ep.description,
    }));
    try {
      await api.request("/ops/apis/batch-save", "POST", undefined, toSave);
      Toast.success(`已保存 ${toSave.length} 个API`);
      setParseVisible(false);
      loadApis();
    } catch (e: any) { Toast.error(e.message); }
  };

  const handleLogout = async () => {
    try { await api.logout(); } catch { }
    navigate("/mis/opsLogin");
  };

  const grouped = apis.reduce((acc, api) => {
    const g = api.groupName || "default";
    if (!acc[g]) acc[g] = [];
    acc[g].push(api);
    return acc;
  }, {} as Record<string, OpsApiItem[]>);

  const fmtJson = (s: string | null | undefined) => { if (!s) return ""; try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; } };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f0f2f5" }}>
      {/* 顶部导航栏 */}
      <div style={{
        padding: "12px 20px", background: "#fff", borderBottom: "1px solid var(--semi-color-border)",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      }}>
        <Text strong style={{ fontSize: 16 }}>运维助手</Text>
        <Button size="small" type="tertiary" onClick={handleLogout}>退出</Button>
      </div>

      {/* 功能导航 */}
      <div style={{ padding: "12px 16px 0" }}>
        <NavCards />
      </div>

      {/* API 管理区 */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left */}
        <div style={{ width: 300, borderRight: "1px solid #e0e0e0", background: "#fff", overflowY: "auto", flexShrink: 0 }}>
          <div style={{ padding: 12, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text strong>API 列表</Text>
            <Space>
              <Button size="small" icon={<IconPlus />} onClick={() => setAddVisible(true)}>新增</Button>
              <Button size="small" icon={<IconSearch />} onClick={() => setParseVisible(true)}>解析</Button>
            </Space>
          </div>
          <Spin spinning={loading}>
            <Collapse accordion style={{ border: "none" }}>
              {Object.entries(grouped).map(([group, items]) => (
                <Collapse.Panel header={<Text strong>{group} ({items.length})</Text>} itemKey={group} key={group}>
                  {items.map(api => (
                    <div key={api.id} onClick={() => selectApi(api)} style={{
                      padding: "6px 12px", cursor: "pointer", fontSize: 12,
                      background: selected?.id === api.id ? "#e6f7ff" : "transparent",
                      borderLeft: `3px solid ${METHOD_COLORS[api.method] || "grey"}`, marginBottom: 2,
                    }}>
                      <Tag size="small" style={{ marginRight: 4 }}>{api.method}</Tag>
                      <span>{api.name}</span>
                      <div style={{ color: "#999", fontSize: 11, marginTop: 2, wordBreak: "break-all" }}>{api.url}</div>
                    </div>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          </Spin>
        </div>

        {/* Right */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {selected ? (
            <div>
              <Space style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 16 }}>{ef.name}</Text>
                <Tag color={METHOD_COLORS[ef.method || "GET"] as any}>{ef.method}</Tag>
                <Button size="small" onClick={doSave}>保存</Button>
                <Button size="small" type="danger" onClick={() => doDelete(selected.id!)}>删除</Button>
              </Space>
              <Card style={{ marginBottom: 12 }}>
                <FieldRow label="名称"><Input value={ef.name} onChange={(v) => setEf({ ...ef, name: v })} /></FieldRow>
                <FieldRow label="方法"><Select value={ef.method} onChange={(v: string) => setEf({ ...ef, method: v })} optionList={METHOD_OPTIONS} style={{ width: 120 }} /></FieldRow>
                <FieldRow label="URL"><Input value={ef.url} onChange={(v) => setEf({ ...ef, url: v })} /></FieldRow>
                <FieldRow label="分组"><Input value={ef.groupName} onChange={(v) => setEf({ ...ef, groupName: v })} /></FieldRow>
                <FieldRow label="Headers"><Input value={ef.headers} onChange={(v) => setEf({ ...ef, headers: v })} /></FieldRow>
                <FieldRow label="Body"><TextArea value={ef.bodyTemplate} onChange={(v) => setEf({ ...ef, bodyTemplate: v })} rows={6} /></FieldRow>
                <FieldRow label="备注"><Input value={ef.remark} onChange={(v) => setEf({ ...ef, remark: v })} /></FieldRow>
              </Card>
              <Button theme="solid" icon={<IconPlay />} onClick={doExec} loading={execLoading} style={{ marginBottom: 12 }}>执行</Button>
              {result && (
                <Card title={`响应 ${result.statusCode} ${result.error ? "- " + result.error : ""}`}>
                  <pre style={{
                    background: "#1e1e1e", color: result.success ? "#d4d4d4" : "#f44747",
                    padding: 12, borderRadius: 6, maxHeight: 400, overflow: "auto",
                    fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all",
                  }}>{fmtJson(result.body) || result.error || "(empty)"}</pre>
                </Card>
              )}
            </div>
          ) : (
            <div style={{ textAlign: "center", marginTop: 100 }}>
              <Text type="tertiary" style={{ fontSize: 16 }}>选择左侧 API 或新增/解析导入</Text>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      <Modal title="手动新增 API" visible={addVisible} onOk={doAdd} onCancel={() => setAddVisible(false)}>
        <FieldRow label="名称"><Input value={af.name} onChange={(v) => setAf({ ...af, name: v })} /></FieldRow>
        <FieldRow label="方法"><Select value={af.method} onChange={(v: string) => setAf({ ...af, method: v })} optionList={METHOD_OPTIONS} style={{ width: 120 }} /></FieldRow>
        <FieldRow label="URL"><Input value={af.url} onChange={(v) => setAf({ ...af, url: v })} /></FieldRow>
        <FieldRow label="分组"><Input value={af.groupName} onChange={(v) => setAf({ ...af, groupName: v })} /></FieldRow>
        <FieldRow label="Headers"><TextArea value={af.headers} onChange={(v) => setAf({ ...af, headers: v })} rows={3} /></FieldRow>
        <FieldRow label="Body"><TextArea value={af.bodyTemplate} onChange={(v) => setAf({ ...af, bodyTemplate: v })} rows={4} /></FieldRow>
        <FieldRow label="备注"><Input value={af.remark} onChange={(v) => setAf({ ...af, remark: v })} /></FieldRow>
      </Modal>

      {/* Parse Modal */}
      <Modal title="解析 Controller" visible={parseVisible} onCancel={() => setParseVisible(false)} footer={null} width={800}>
        <Space style={{ marginBottom: 12, width: "100%" }}>
          <Input placeholder="Controller 类名（模糊匹配）" value={parseClass} onChange={setParseClass} style={{ width: 300 }} />
          <Button theme="solid" onClick={doParse} loading={parseLoading}>解析</Button>
          {selParsed.length > 0 && <Button onClick={doBatchSave}>保存选中 ({selParsed.length})</Button>}
        </Space>
        {parsed.length > 0 && (
          <Table
            dataSource={parsed.map((ep, i) => ({ ...ep, key: i }))}
            rowSelection={{ selectedRowKeys: selParsed, onChange: (keys) => setSelParsed(keys as number[]) }}
            columns={[
              { title: "类", dataIndex: "className", width: 150 },
              { title: "方法", dataIndex: "method", width: 70, render: (v: string) => <Tag color={METHOD_COLORS[v] as any || "grey"}>{v}</Tag> },
              { title: "URL", dataIndex: "url" },
              { title: "说明", dataIndex: "description", width: 150 },
            ]}
            size="small" pagination={false}
          />
        )}
      </Modal>
    </div>
  );
}

/* ==================== 根组件（带登录守卫） ==================== */

export default function OpsAssistant() {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      const token = api.getToken();
      if (!token) { setChecking(false); return; }
      try {
        await api.request("/auth/user", "GET");
      } catch {
        // token无效，需要重新登录
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  if (checking) return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Spin size="large" /></div>;
  return <OpsMain />;
}
